import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { DEFAULT_SAME_AS_RETURNING_ITEMS_FROM_TEXT } from "@redotech/redo-customer-portal-shared/address-copy";
import { AddressInfo } from "@redotech/redo-customer-portal-shared/hooks/useAddress/use-address";
import {
  useAddressDiff,
  useVerifyAddress,
} from "@redotech/redo-customer-portal-shared/hooks/useVerifyAddress/use-verify-address";
import { ReturnAddressType } from "@redotech/redo-customer-portal-shared/navigator";
import { checkBlockingAddressErrors } from "@redotech/redo-customer-portal-shared/utils/address-validation";
import { useTranslation } from "@redotech/redo-i18n";
import { Address as AddressType } from "@redotech/redo-model/draft-return/draft-return";
import {
  AddressSelection,
  toAddressFields,
} from "@redotech/redo-web/address-verification/address-suggestion";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { filterTruthy } from "@redotech/util/array";
import { memo, useEffect, useMemo, useState } from "react";
import { AddressDisplay } from "./address-display";

export interface AddressValidationStatus {
  type: ReturnAddressType;
  isInvalid: boolean;
}

export function Address({
  addressInfo,
  onAddressSelected,
  onValidationStatusChanged,
  continueDisabled = false,
  showBorder = true,
  skipComparison = false,
}: {
  addressInfo: AddressInfo;
  onAddressSelected?: (type: AddressInfo["type"], address: AddressType) => void;
  onValidationStatusChanged?: (status: AddressValidationStatus) => void;
  continueDisabled?: boolean;
  showBorder?: boolean;
  skipComparison?: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<AddressSelection>("suggested");
  const screenSize = useScreenType();
  const verifyAddressQuery = useVerifyAddress({
    address: addressInfo.address ?? undefined,
  });

  const blockingError = useMemo(() => {
    return checkBlockingAddressErrors(
      addressInfo.address?.street1 ?? undefined,
      addressInfo.address?.street2 ?? undefined,
      addressInfo.address?.country ?? undefined,
      addressInfo.type,
    );
  }, [
    addressInfo.address?.street1,
    addressInfo.address?.street2,
    addressInfo.address?.country,
    addressInfo.type,
  ]);

  const hasVerificationError =
    (verifyAddressQuery.isSuccess &&
      !verifyAddressQuery.data?.verifications?.delivery?.success) ||
    verifyAddressQuery.isError;

  const isLoading = verifyAddressQuery.isPending;
  const suggestedAddress = verifyAddressQuery.data?.verifiedAddress;
  const { changedFields, changed: addressChanged } = useAddressDiff(
    addressInfo.address ?? undefined,
    suggestedAddress,
  );
  const isMissingEmail = !addressInfo.address?.email;

  const showComparison =
    !skipComparison &&
    !blockingError &&
    !!addressInfo.address &&
    (isLoading || hasVerificationError || addressChanged);

  useEffect(() => {
    if (onAddressSelected && addressInfo.address) {
      if (selected === "suggested" && suggestedAddress) {
        onAddressSelected(addressInfo.type, {
          ...suggestedAddress,
          name: addressInfo.address.name,
          email: addressInfo.address.email,
          phone: addressInfo.address.phone,
        });
      } else {
        onAddressSelected(addressInfo.type, addressInfo.address);
      }
    }
    onValidationStatusChanged?.({
      type: addressInfo.type,
      isInvalid: hasVerificationError,
    });
  }, [
    selected,
    suggestedAddress,
    addressInfo.address,
    addressInfo.type,
    onAddressSelected,
    onValidationStatusChanged,
    hasVerificationError,
  ]);

  return (
    <AddressDisplay
      addressFields={
        addressInfo.address
          ? {
              ...toAddressFields(addressInfo.address),
              name: addressInfo.address.name,
            }
          : undefined
      }
      blockingErrorMessage={blockingError?.message}
      changedFields={changedFields}
      comparisonDirection={screenSize === ScreenType.MOBILE ? "column" : "row"}
      comparisonDisabled={isLoading || hasVerificationError}
      comparisonHasError={hasVerificationError}
      comparisonIsLoading={isLoading}
      continueDisabled={continueDisabled}
      missingEmailErrorMessage={
        continueDisabled && isMissingEmail ? t("Email is required.") : undefined
      }
      onEdit={addressInfo.edit}
      onSelect={setSelected}
      selected={selected}
      showBorder={showBorder}
      showComparison={showComparison}
      suggestedAddressFields={
        suggestedAddress ? toAddressFields(suggestedAddress) : undefined
      }
      title={addressInfo.title}
    />
  );
}

export const ShippingLineOfText = memo(function ShippingLineOfText({
  lineOfText,
}: {
  lineOfText: string;
}) {
  return (
    <Text fontSize="md" fontWeight="medium">
      {lineOfText}
    </Text>
  );
});

export const AddressOld = memo(function Address({
  address,
  title,
  edit,
}: {
  address?: AddressType;
  title: string;
  edit?: () => void;
}) {
  const { t } = useTranslation();
  let secondLine = "";
  if (address) {
    secondLine = filterTruthy([address.city, address.state]).join(", ");
    if (address.zip) {
      secondLine += ` ${address.zip}`;
    }
  }
  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      data-ui="address"
      dir="column"
      gap="none"
      radius="md"
    >
      <Text fontSize="sm" textColor="tertiary">
        {title}
      </Text>
      {address && (
        <Flex dir="column" gap="none">
          <ShippingLineOfText lineOfText={address.name} />
          <ShippingLineOfText lineOfText={address.street1} />
          <ShippingLineOfText lineOfText={address.street2 || ""} />
          <ShippingLineOfText lineOfText={secondLine} />
          <ShippingLineOfText lineOfText={address.country} />
        </Flex>
      )}
      {!address && (
        <ShippingLineOfText
          lineOfText={t(DEFAULT_SAME_AS_RETURNING_ITEMS_FROM_TEXT)}
        />
      )}
      {edit && (
        <RedoButton
          hierarchy="tertiary"
          onClick={() => edit()}
          text={t("Edit")}
        />
      )}
    </Flex>
  );
});
